import { memo, useState } from "react";
import { Button, Header, Icon, Image, Label, Modal } from "semantic-ui-react";

// Components
import Emulator from "./Emulator";

// Commons
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

// Serializers
import DiagRuleSerializer from "@/issara-sdk/types/DiagRuleSerializer_apps_PRX";

// Styles
import "./styles/CardMeFormDetail.scss";

// Interface
import { STYLES, ICONS, BTN_ACTS } from "./sequence/MeForm";

// Types
import { PickedState, SetProp, SITE_HOST } from "./MeFormType";

type HeaderFormDetailProps = {
  // data
  formDetail?: DiagRuleSerializer;
  
  // permissions / flags
  isActive: boolean;
  isPublished: boolean;

  // Infra
  setProp: SetProp;
  selectedDivision: any;

  // actions
  onGoBack: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onPublish: () => void;
} & Pick<PickedState, "buttonLoadCheck">;

const HeaderFormDetail = (props: HeaderFormDetailProps) => {
  const { formDetail } = props;

  const [copiedLink, setCopiedLink] = useState<boolean>(false);

  const handleCopyLink = async () => {
    if (!props.isPublished) return;

    const formName = formDetail?.name || "";
    const formPath =
      formDetail?.link?.replace("/form/", "/assessment-form/");
    const link = `${SITE_HOST}${formPath}`;

    if (!formName || !link) return;

    const message = `Take a moment to complete the assessment - [${formName}](${link}) - to help us track and understand you health more accurately.`;

    try {
      await navigator.clipboard.writeText(message);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 1500);
    } catch (err) {
      console.error("Failed to copy link: ", err);
    }
  };

  return (
    <div className="card-meform-detail-header">
      <div className="left-group">
        <ButtonLoadCheck
          className="back-btn"
          title={<Icon name="angle left" />}
          onClick={props.onGoBack}
        />
        <Header className="form-title" as="h2">
          {formDetail?.name || ""}
        </Header>
        <Image
          className="edit-icon"
          src={ICONS.edit}
          onClick={props.onEdit}
        />
        <Label
          style={props.isPublished ? STYLES.published : STYLES.unpublished}
          content={props.isPublished ? "Published" : "Unpublished"}
        />
        {props.isPublished && (
          <Image
            className="duplicate-icon"
            src={ICONS.duplicate}
            onClick={props.onDuplicate}
          />
        )}
      </div>

      <div className="right-group">
        <Button
          className="copy-link-btn"
          size="tiny"
          color="pink"
          disabled={!props.isPublished}
          onClick={handleCopyLink}
        >
          <Icon name={copiedLink ? "check" : "copy outline"} />
          {copiedLink ? "Copied!" : "Copy Link"}
        </Button>
        <Modal
          style={{ width: "auto", height: "auto" }}
          trigger={
            <Button size="tiny" style={STYLES.primaryThemeBasicBtn}>
              <div className="card-meform-detail-btn-label">
                <Image className="test-run-icon" src={ICONS.testRun} />
                <label>Test Run</label>
              </div>
            </Button>
          }
        >
          <Emulator
            data={JSON.parse(formDetail?.content ?? "{}")}
            name={formDetail?.name}
            divisionName={props.selectedDivision?.name}
          />
        </Modal>
        <ButtonLoadCheck
          // func
          setProp={props.setProp}
          paramKey={BTN_ACTS.PUBLISH_FORM}
          buttonLoadCheck={props.buttonLoadCheck?.[`${BTN_ACTS.PUBLISH_FORM}`]}
          // config
          size="tiny"
          style={{
            ...(STYLES.primaryThemeBasicBtn),
            display: formDetail?.active ? undefined : "none"
          }}
          disabled={props.isPublished}
          onClick={props.onPublish}
          title={
            <div className="card-meform-detail-btn-label">
              <Image className="publish-icon" src={ICONS.publish} />
              <label>Publish</label>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default memo(HeaderFormDetail);